import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button
} from "@mui/material";
import produce from "immer";
import cloneDeep from "lodash/cloneDeep";
import QuestionnaireRow, { defaultRow, QUESTIONNAIRE } from "./QuestionnaireRow";
import { tl } from "../../../components/translate";
import { Questionnaire } from "../../../interfaces/AssessmentInterfaces";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";

interface QuestionnairePopUpInterface {
  isOpen: boolean;
  onClose: () => void;
  previousQuestionnaire?: Questionnaire[];
  onSave: (data: Questionnaire[]) => void;
}

const getRemainingDefaultQuestionnaires = (
  previousQuestionnaire: Questionnaire[],
  configuredQuestions: Questionnaire[]
) =>
  configuredQuestions.filter(
    (q: Questionnaire) =>
      !previousQuestionnaire.some((prevQ: Questionnaire) => prevQ.name === q.name)
  );

const QuestionnairePopUp: React.FC<QuestionnairePopUpInterface> = ({
  isOpen,
  onClose,
  previousQuestionnaire = [],
  onSave
}) => {
  const currentResourceCentre = useCurrentResourceCentre();
  const [questionnaire, setQuestionnaire] = React.useState<Questionnaire[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const questionnairesOrder =
    currentResourceCentre?.settings?.assessmentSettings?.questionnairesOrder || [];
  const questionnaireSettings = currentResourceCentre?.settings?.questionnaireSettings;

  const defaultQuestionnaires = React.useMemo((): Questionnaire[] => {
    const defaultArr: Questionnaire[] = [];

    if (questionnaireSettings && questionnairesOrder) {
      questionnairesOrder.forEach((qKey: string) => {
        if (questionnaireSettings[qKey] !== false) {
          const qInfo = QUESTIONNAIRE.find((q) => q.dbKey === qKey);
          if (qInfo) {
            defaultArr.push({
              ...qInfo,
              reading: qInfo.defaultValue || ""
            });
          } else {
            // This is a custom question added in settings
            defaultArr.push({
              ...defaultRow,
              name: qKey,
              label: qKey,
              dbKey: qKey
            });
          }
        }
      });
    } else if (!questionnairesOrder || questionnairesOrder.length === 0) {
      return cloneDeep(QUESTIONNAIRE);
    }

    return defaultArr;
  }, [questionnairesOrder, questionnaireSettings]);

  React.useEffect(() => {
    setQuestionnaire(previousQuestionnaire?.length ? previousQuestionnaire : defaultQuestionnaires);
  }, [previousQuestionnaire, defaultQuestionnaires]);

  return (
    <Dialog fullWidth onClose={onClose} open={isOpen}>
      <DialogTitle
        style={{
          borderBottom: "1px solid #ececec",
          background: "#f9f9f9",
          textTransform: "uppercase"
        }}
      >
        {tl("assessment.basicHealthQuestionnaire")}
      </DialogTitle>
      <DialogContent style={{ padding: "24px 32px" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2
          }}
        >
          <Box sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
            <Typography style={{ fontWeight: 600, fontSize: "16px" }}>
              {tl("assessment.testName")}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
            <Typography
              style={{
                fontWeight: 600,
                fontSize: "16px"
              }}
            >
              {tl("assessment.reading")}
            </Typography>
          </Box>
        </Box>

        <Box width="100%" mb={1}>
          {questionnaire.map((question, index) => (
            <QuestionnaireRow
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              question={question}
              remainingQuestionnaires={getRemainingDefaultQuestionnaires(
                questionnaire,
                defaultQuestionnaires
              )}
              onRemove={() => {
                const updatedQuestionnaire = produce(questionnaire, (draft) => {
                  draft.splice(index, 1);
                });
                setQuestionnaire(updatedQuestionnaire);
              }}
              onChange={(v) => {
                const updatedQuestionnaire = produce(questionnaire, (draft) => {
                  draft[index] = v;
                });
                setQuestionnaire(updatedQuestionnaire);
              }}
            />
          ))}
        </Box>

        <Button
          data-testmation="questionnaire.AddNewButton"
          onClick={() => {
            setQuestionnaire([...questionnaire, { ...defaultRow }]);
          }}
          variant="outlined"
        >
          {tl("assessment.addNew")}
        </Button>
      </DialogContent>
      <DialogActions
        style={{
          background: "#00800014",
          borderTop: "1px solid #00800017",
          justifyContent: "flex-start"
        }}
      >
        <Box
          width="100%"
          display="flex"
          justifyContent="flex-end"
          padding="0px 24px"
          alignContent="center"
        >
          <Button
            style={{ marginRight: "8px" }}
            data-testmation="questionnaire.cancelButton"
            color="primary"
            onClick={() => {
              onClose();
            }}
          >
            {tl("assessment.cancel")}
          </Button>
          <Button
            data-testmation="questionnaireSaveButton"
            variant="contained"
            color="primary"
            onClick={() => {
              const answeredQuestionList = questionnaire.filter((qi) => qi.name && qi.reading);
              onSave(answeredQuestionList);
              setQuestionnaire(defaultQuestionnaires);
              onClose();
            }}
          >
            {tl("assessment.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionnairePopUp;
